import { useMemo, useRef, useState, type FormEvent } from 'react';
import {
  Boxes,
  CheckCircle2,
  FileText,
  HardHat,
  Loader2,
  Paperclip,
  Pencil,
  Plus,
  RotateCcw,
  Trash2,
  Upload,
  Wallet,
} from 'lucide-react';
import { AttachmentViewer, type ViewableFile } from '@/components/attachment-viewer';
import { Badge } from '@/components/badge';
import { Button } from '@/components/button';
import { Card, CardBody, CardHeader } from '@/components/card';
import { Dialog } from '@/components/dialog';
import { EmptyState } from '@/components/empty-state';
import { FileDrop } from '@/components/file-drop';
import { Input } from '@/components/input';
import { Menu, type MenuItem } from '@/components/menu';
import { Select } from '@/components/select';
import { SkeletonList } from '@/components/skeleton';
import { Textarea } from '@/components/textarea';
import { useConfirm } from '@/components/confirm';
import { useToast } from '@/components/toast';
import { useAccounts } from '@/lib/accounting';
import { extractMessage, useFormError } from '@/lib/form-error';
import {
  centsToDollarsInput,
  dollars,
  dollarsToCents,
  formatBytes,
  formatDate,
} from '@/lib/format';
import {
  useAddFixedAssetCost,
  useAssetClasses,
  useCreateFixedAsset,
  useDeleteFixedAsset,
  useDeleteFixedAssetEvidence,
  useFixedAssets,
  usePlaceInService,
  useRemoveFixedAssetCost,
  useRemoveFromService,
  useUpdateFixedAsset,
  useUploadFixedAssetEvidence,
  type NewAssetInput,
} from '@/lib/fixed-assets';
import type {
  AssetClass,
  AssetClassData,
  FixedAssetCostData,
  FixedAssetData,
} from '@/types/api';

/**
 * The fixed asset register: things bought or built whose cost comes off over
 * years rather than all at once.
 *
 * Assets under construction sort first and are the ones that need attention,
 * because nothing depreciates until the day an asset is placed in service and
 * that date has to be recorded by hand.
 */
export function FixedAssetsTab() {
  const { data, isLoading } = useFixedAssets();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<FixedAssetData | null>(null);
  const [placing, setPlacing] = useState<FixedAssetData | null>(null);
  const [addingCost, setAddingCost] = useState<FixedAssetData | null>(null);

  const assets = data ?? [];
  const building = assets.filter((a) => a.status === 'in_progress');
  const inService = assets.filter((a) => a.status !== 'in_progress');

  return (
    <div className="space-y-5">
      <Card>
        <CardHeader className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold">Fixed assets</h3>
            <p className="text-xs text-[var(--color-foreground-muted)]">
              Anything with a life beyond this year. Its cost is recovered through depreciation
              (Schedule C line 13), not deducted the year it was spent.
            </p>
          </div>
          <Button onClick={() => setCreating(true)}>
            <Plus className="size-4" /> New asset
          </Button>
        </CardHeader>
        <CardBody>
          {isLoading ? (
            <SkeletonList rows={3} />
          ) : assets.length === 0 ? (
            <EmptyState
              icon={Boxes}
              title="Nothing capitalized yet"
              description="Start one here, or capitalize a charge straight from the Transactions review queue."
            />
          ) : (
            <div className="space-y-5">
              {building.length > 0 ? (
                <Section
                  title="Under construction"
                  hint="Costs are accumulating in 1400. Nothing depreciates until it is placed in service."
                  assets={building}
                  onEdit={setEditing}
                  onPlace={setPlacing}
                  onAddCost={setAddingCost}
                />
              ) : null}
              {inService.length > 0 ? (
                <Section
                  title="In service"
                  hint="Basis frozen and the clock running."
                  assets={inService}
                  onEdit={setEditing}
                  onPlace={setPlacing}
                  onAddCost={setAddingCost}
                />
              ) : null}
            </div>
          )}
        </CardBody>
      </Card>

      {creating ? <AssetDialog onClose={() => setCreating(false)} /> : null}
      {editing ? <AssetDialog asset={editing} onClose={() => setEditing(null)} /> : null}
      {placing ? (
        <PlaceInServiceDialog asset={placing} onClose={() => setPlacing(null)} />
      ) : null}
      {addingCost ? (
        <AddCostDialog asset={addingCost} onClose={() => setAddingCost(null)} />
      ) : null}
    </div>
  );
}

function Section({
  title,
  hint,
  assets,
  onEdit,
  onPlace,
  onAddCost,
}: {
  title: string;
  hint: string;
  assets: FixedAssetData[];
  onEdit: (a: FixedAssetData) => void;
  onPlace: (a: FixedAssetData) => void;
  onAddCost: (a: FixedAssetData) => void;
}) {
  return (
    <div className="space-y-2">
      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wider text-[var(--color-foreground-muted)]">
          {title}
        </h4>
        <p className="text-xs text-[var(--color-foreground-subtle)]">{hint}</p>
      </div>
      <div className="divide-y divide-[var(--color-border)] rounded-md border border-[var(--color-border)]">
        {assets.map((asset) => (
          <AssetRow
            key={asset.id}
            asset={asset}
            onEdit={onEdit}
            onPlace={onPlace}
            onAddCost={onAddCost}
          />
        ))}
      </div>
    </div>
  );
}

function AssetRow({
  asset,
  onEdit,
  onPlace,
  onAddCost,
}: {
  asset: FixedAssetData;
  onEdit: (a: FixedAssetData) => void;
  onPlace: (a: FixedAssetData) => void;
  onAddCost: (a: FixedAssetData) => void;
}) {
  const removeFromService = useRemoveFromService();
  const del = useDeleteFixedAsset();
  const confirm = useConfirm();
  const toast = useToast();
  const [open, setOpen] = useState(false);

  const inProgress = asset.status === 'in_progress';
  const total = inProgress ? asset.capitalized_cents : asset.basis_cents;

  const items: MenuItem[] = [
    { key: 'edit', label: 'Edit', icon: Pencil, onSelect: () => onEdit(asset) },
    ...(inProgress
      ? [
          {
            key: 'add-cost',
            label: 'Add cost paid elsewhere',
            icon: Wallet,
            onSelect: () => onAddCost(asset),
          },
          {
            key: 'place',
            label: 'Place in service',
            icon: CheckCircle2,
            disabled: asset.capitalized_cents === 0,
            onSelect: () => onPlace(asset),
          },
        ]
      : [
          {
            key: 'remove',
            label: 'Take back out of service',
            icon: RotateCcw,
            onSelect: async () => {
              const ok = await confirm({
                title: `Take ${asset.name} back out of service?`,
                body: 'The basis and the year-one election are cleared and the cost returns to construction in progress. Use this when the in-service date is wrong, since that date decides which tax year the deduction lands in.',
                confirmLabel: 'Take out of service',
              });
              if (ok) {
                await removeFromService.mutateAsync(asset.id);
                toast.success(`${asset.name} is back under construction`);
              }
            },
          },
        ]),
    {
      key: 'delete',
      label: 'Delete asset',
      icon: Trash2,
      danger: true,
      // Every cost carries a posted entry crediting whatever paid for it; the
      // server refuses rather than stranding those.
      disabled: asset.costs.length > 0,
      onSelect: async () => {
        const ok = await confirm({
          title: `Delete ${asset.name}?`,
          body: 'Removes it from the register.',
          confirmLabel: 'Delete asset',
          tone: 'danger',
        });
        if (ok) {
          await del.mutateAsync(asset.id);
          toast.success('Asset deleted');
        }
      },
    },
  ];

  return (
    <div className="px-3 py-2.5">
      <div className="flex items-start justify-between gap-3">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="min-w-0 flex-1 text-left"
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate text-sm font-medium">{asset.name}</span>
            <Badge tone="neutral">{asset.asset_class_label}</Badge>
            {inProgress ? (
              <Badge tone="warning">
                <HardHat className="size-3" /> Building
              </Badge>
            ) : asset.status === 'disposed' ? (
              <Badge tone="neutral">Disposed</Badge>
            ) : (
              <Badge tone="success">In service</Badge>
            )}
          </div>
          <div className="mt-0.5 text-xs text-[var(--color-foreground-muted)]">
            {asset.recovery_years}-year
            {asset.placed_in_service_on
              ? ` · in service ${formatDate(asset.placed_in_service_on)}`
              : ` · ${asset.costs.length} cost${asset.costs.length === 1 ? '' : 's'} so far`}
            {asset.business_use_pct < 100 ? ` · ${asset.business_use_pct}% business use` : ''}
          </div>
        </button>
        <div className="flex shrink-0 items-center gap-2">
          <span className="text-sm font-semibold tabular-nums">{dollars(total)}</span>
          <Menu items={items} />
        </div>
      </div>

      {open ? <AssetDetail asset={asset} /> : null}
    </div>
  );
}

/** The components that add up to the basis, the receipts behind them, and how year one was taken. */
function AssetDetail({ asset }: { asset: FixedAssetData }) {
  const removeCost = useRemoveFixedAssetCost();
  const confirm = useConfirm();
  const toast = useToast();
  const [viewing, setViewing] = useState<ViewableFile | null>(null);
  const inProgress = asset.status === 'in_progress';

  async function onRemoveCost(cost: FixedAssetCostData) {
    const ok = await confirm({
      title: `Remove ${cost.component}?`,
      body: `Takes ${dollars(cost.amount_cents)} back off ${asset.name} and deletes the journal entry it posted.`,
      confirmLabel: 'Remove cost',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await removeCost.mutateAsync({ assetId: asset.id, costId: cost.id });
      toast.success('Cost removed');
    } catch (err) {
      toast.error(extractMessage(err));
    }
  }

  return (
    <div className="mt-2.5 space-y-2.5 border-t border-[var(--color-border)] pt-2.5">
      {asset.costs.length === 0 ? (
        <p className="text-xs italic text-[var(--color-foreground-subtle)]">
          Nothing capitalized against this yet. Capitalize a charge from the Transactions review
          queue, or use Add cost paid elsewhere in this row's menu.
        </p>
      ) : (
        <ul className="space-y-1">
          {asset.costs.map((cost) => (
            <li
              key={cost.id}
              className="flex items-baseline justify-between gap-3 text-xs"
            >
              <span className="min-w-0 truncate">
                <span className="font-medium">{cost.component}</span>
                {cost.source_label ? (
                  <span className="text-[var(--color-foreground-subtle)]"> · {cost.source_label}</span>
                ) : null}
                <span className="text-[var(--color-foreground-subtle)]">
                  {' '}
                  · {formatDate(cost.incurred_on)}
                </span>
                {cost.memo ? (
                  <span className="text-[var(--color-foreground-subtle)]"> · {cost.memo}</span>
                ) : null}
              </span>
              <span className="flex shrink-0 items-center gap-1.5">
                {asset.evidence
                  .filter((file) => file.fixed_asset_cost_id === cost.id)
                  .map((file) => (
                    <button
                      key={file.id}
                      type="button"
                      onClick={() => setViewing(file)}
                      title={file.file_name}
                      aria-label={`View receipt for ${cost.component}`}
                      className="flex size-6 items-center justify-center rounded-md text-[var(--color-foreground-subtle)] transition-colors hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-foreground)]"
                    >
                      <Paperclip className="size-3" />
                    </button>
                  ))}
                <span className="tabular-nums">{dollars(cost.amount_cents)}</span>
                {/* Bank-sourced costs come off by unposting the charge in Transactions. */}
                {inProgress && cost.bank_transaction_id == null ? (
                  <button
                    type="button"
                    onClick={() => void onRemoveCost(cost)}
                    disabled={removeCost.isPending}
                    aria-label={`Remove ${cost.component}`}
                    className="flex size-6 items-center justify-center rounded-md text-[var(--color-foreground-subtle)] transition-colors hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-danger)] disabled:opacity-50"
                  >
                    <Trash2 className="size-3" />
                  </button>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      )}

      <AssetEvidence asset={asset} onView={setViewing} />
      <AttachmentViewer file={viewing} onClose={() => setViewing(null)} />

      {asset.status !== 'in_progress' ? (
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-4">
          <Figure label="Basis" value={dollars(asset.basis_cents)} />
          <Figure label="§179" value={dollars(asset.section_179_cents)} />
          <Figure label="Bonus" value={dollars(asset.bonus_cents)} />
          <Figure label="Left on MACRS" value={dollars(asset.macrs_basis_cents)} />
        </div>
      ) : null}

      <p className="text-[11px] text-[var(--color-foreground-subtle)]">{asset.year_one_note}</p>
    </div>
  );
}

/**
 * Receipts and photos kept on the asset. For a cost paid outside the linked
 * accounts this is the only record of the purchase, since no bank transaction
 * holds a receipt for it.
 */
function AssetEvidence({
  asset,
  onView,
}: {
  asset: FixedAssetData;
  onView: (file: ViewableFile) => void;
}) {
  const upload = useUploadFixedAssetEvidence();
  const remove = useDeleteFixedAssetEvidence();
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file after an error
    if (!file) return;
    try {
      await upload.mutateAsync({ assetId: asset.id, file });
      toast.success('File attached');
    } catch (err) {
      toast.error(extractMessage(err));
    }
  }

  async function onDelete(mediaId: number) {
    try {
      await remove.mutateAsync({ assetId: asset.id, mediaId });
      toast.success('File removed');
    } catch (err) {
      toast.error(extractMessage(err));
    }
  }

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-[var(--color-foreground-muted)]">
          <Paperclip className="size-3" />
          Files
        </span>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={upload.isPending}
          className="inline-flex items-center gap-1 text-xs font-medium text-[var(--color-primary)] hover:underline disabled:opacity-50"
        >
          {upload.isPending ? (
            <Loader2 className="size-3 animate-spin" />
          ) : (
            <Upload className="size-3" />
          )}
          {upload.isPending ? 'Uploading…' : 'Attach file'}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*,application/pdf"
          className="hidden"
          onChange={onPick}
        />
      </div>

      {asset.evidence.length === 0 ? (
        <p className="text-xs text-[var(--color-foreground-subtle)]">
          No receipts or photos yet. Attach the receipt for anything paid outside your linked
          accounts, since this is the only place it is kept.
        </p>
      ) : (
        <ul className="space-y-1">
          {asset.evidence.map((file) => (
            <li
              key={file.id}
              className="flex items-center gap-2 rounded-md border border-[var(--color-border)] px-2.5 py-1.5"
            >
              {file.is_image ? (
                <button type="button" onClick={() => onView(file)} className="shrink-0">
                  <img src={file.url} alt={file.file_name} className="size-8 rounded object-cover" />
                </button>
              ) : (
                <FileText className="size-5 shrink-0 text-[var(--color-foreground-muted)]" />
              )}
              <button
                type="button"
                onClick={() => onView(file)}
                className="min-w-0 flex-1 truncate text-left text-sm hover:underline"
              >
                {file.file_name}
              </button>
              <span className="shrink-0 text-xs tabular-nums text-[var(--color-foreground-muted)]">
                {formatBytes(file.file_size)}
              </span>
              <button
                type="button"
                onClick={() => void onDelete(file.id)}
                disabled={remove.isPending}
                aria-label={`Remove ${file.file_name}`}
                className="flex size-7 shrink-0 items-center justify-center rounded-md text-[var(--color-foreground-muted)] transition-colors hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-danger)] disabled:opacity-50"
              >
                <Trash2 className="size-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[var(--color-foreground-subtle)]">{label}</div>
      <div className="font-medium tabular-nums">{value}</div>
    </div>
  );
}

function AssetDialog({ asset, onClose }: { asset?: FixedAssetData; onClose: () => void }) {
  const classes = useAssetClasses();
  const create = useCreateFixedAsset();
  const update = useUpdateFixedAsset();
  const toast = useToast();
  const { error, wrap } = useFormError();

  const [name, setName] = useState(asset?.name ?? '');
  const [assetClass, setAssetClass] = useState<AssetClass>(
    (asset?.asset_class as AssetClass) ?? 'equipment_7',
  );
  const [acquiredOn, setAcquiredOn] = useState(asset?.acquired_on ?? '');
  const [description, setDescription] = useState(asset?.description ?? '');

  const chosen = useMemo(
    () => (classes.data ?? []).find((c) => c.value === assetClass),
    [classes.data, assetClass],
  );
  // Reclassifying after the fact would restate a basis that is already
  // depreciating, so the server refuses it too.
  const classLocked = asset != null && asset.status !== 'in_progress';

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const payload: NewAssetInput = {
      name,
      asset_class: assetClass,
      acquired_on: acquiredOn || null,
      description: description || null,
    };
    const ok = await wrap(() =>
      asset
        ? update.mutateAsync({ id: asset.id, ...payload })
        : create.mutateAsync(payload),
    );
    if (ok) {
      toast.success(asset ? 'Asset updated' : 'Asset added');
      onClose();
    }
  }

  return (
    <Dialog open onClose={onClose} title={asset ? `Edit ${asset.name}` : 'New fixed asset'}>
      <form className="space-y-3" onSubmit={handleSubmit}>
        <Input
          required
          autoFocus
          placeholder="What is it? e.g. Container office"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />

        <label className="block space-y-1">
          <span className="text-xs font-medium text-[var(--color-foreground-muted)]">Class</span>
          <Select
            value={assetClass}
            disabled={classLocked}
            onChange={(e) => setAssetClass(e.target.value as AssetClass)}
          >
            {(classes.data ?? []).map((c) => (
              <option key={c.value} value={c.value}>
                {c.label} · {c.recovery_years}-year
              </option>
            ))}
          </Select>
          {classLocked ? (
            <span className="block text-[11px] text-[var(--color-foreground-subtle)]">
              Take the asset back out of service to change its class.
            </span>
          ) : (
            <ClassNote chosen={chosen} />
          )}
        </label>

        <label className="block space-y-1">
          <span className="text-xs font-medium text-[var(--color-foreground-muted)]">
            Acquired on
          </span>
          <Input type="date" value={acquiredOn} onChange={(e) => setAcquiredOn(e.target.value)} />
        </label>

        <Textarea
          placeholder="Notes, and anything that explains how it is installed."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />

        {error ? <p className="text-sm text-[var(--color-danger)]">{error}</p> : null}
        <div className="flex justify-end">
          <Button type="submit" disabled={create.isPending || update.isPending}>
            {create.isPending || update.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/**
 * A cost paid outside the linked bank accounts: a personal card, cash, or an
 * account linked after the purchase. What paid for it is the other half of
 * the entry, so it is asked for; personal money put into the business is
 * Owner's Capital.
 */
function AddCostDialog({ asset, onClose }: { asset: FixedAssetData; onClose: () => void }) {
  const accounts = useAccounts();
  const add = useAddFixedAssetCost();
  const toast = useToast();
  const { error, wrap } = useFormError();

  // Balance-sheet accounts outside the register's own 1400-1599 block: the
  // same rule FixedAsset::isCapitalAccountCode enforces on the server.
  const payers = useMemo(
    () =>
      (accounts.data ?? []).filter(
        (a) =>
          ['asset', 'liability', 'equity'].includes(a.type) &&
          !(a.code.length === 4 && a.code >= '1400' && a.code <= '1599'),
      ),
    [accounts.data],
  );

  const [component, setComponent] = useState('');
  const [amount, setAmount] = useState('');
  const [incurredOn, setIncurredOn] = useState(asset.acquired_on ?? '');
  const [paidFrom, setPaidFrom] = useState<number | null>(null);
  const [memo, setMemo] = useState('');
  const [receipt, setReceipt] = useState<File | null>(null);

  // Owner's Capital until something else is picked: personal money is the
  // usual reason a cost comes in this way rather than through the bank feed.
  const paidFromId = paidFrom ?? payers.find((a) => a.code === '3000')?.id ?? null;

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const ok = await wrap(() =>
      add.mutateAsync({
        assetId: asset.id,
        component,
        amount_cents: dollarsToCents(amount) ?? 0,
        incurred_on: incurredOn,
        paid_from_account_id: paidFromId ?? 0,
        memo: memo || null,
        receipt,
      }),
    );
    if (ok) {
      toast.success(`Added to ${asset.name}`);
      onClose();
    }
  }

  return (
    <Dialog open onClose={onClose} title={`Add a cost to ${asset.name}`} size="sm">
      <form className="space-y-3" onSubmit={handleSubmit}>
        <p className="text-xs text-[var(--color-foreground-muted)]">
          For something paid outside your linked accounts, like a personal card or cash. A charge
          that came through the bank feed is capitalized from Transactions instead.
        </p>

        <Input
          required
          autoFocus
          placeholder="What was it? e.g. Container, delivery"
          value={component}
          onChange={(e) => setComponent(e.target.value)}
        />

        <div className="grid grid-cols-2 gap-3">
          <label className="block space-y-1">
            <span className="text-xs font-medium text-[var(--color-foreground-muted)]">Amount</span>
            <Input
              required
              inputMode="decimal"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </label>
          <label className="block space-y-1">
            <span className="text-xs font-medium text-[var(--color-foreground-muted)]">Paid on</span>
            <Input
              type="date"
              required
              value={incurredOn}
              onChange={(e) => setIncurredOn(e.target.value)}
            />
          </label>
        </div>

        <label className="block space-y-1">
          <span className="text-xs font-medium text-[var(--color-foreground-muted)]">Paid from</span>
          <Select
            required
            value={paidFromId ?? ''}
            onChange={(e) => setPaidFrom(Number(e.target.value))}
          >
            <option value="" disabled>
              {accounts.isLoading ? 'Loading…' : 'Pick an account…'}
            </option>
            {payers.map((a) => (
              <option key={a.id} value={a.id}>
                {a.code} · {a.name}
              </option>
            ))}
          </Select>
          <span className="block text-[11px] text-[var(--color-foreground-subtle)]">
            Owner's Capital for your own money put into the business, including a personal card you
            paid off yourself.
          </span>
        </label>

        <Input
          placeholder="Note, e.g. which card or check (optional)"
          value={memo}
          onChange={(e) => setMemo(e.target.value)}
        />

        <div className="space-y-1">
          <span className="text-xs font-medium text-[var(--color-foreground-muted)]">
            Receipt
          </span>
          <FileDrop
            file={receipt}
            onChange={setReceipt}
            accept="image/*,application/pdf"
            hint="PDF or image up to 10 MB. With no bank charge behind it, this is the record."
          />
        </div>

        {error ? <p className="text-sm text-[var(--color-danger)]">{error}</p> : null}
        <div className="flex justify-end">
          <Button type="submit" disabled={add.isPending || paidFromId == null}>
            {add.isPending ? 'Saving…' : 'Add cost'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/** What the chosen class means, said at the moment the choice is made. */
export function ClassNote({ chosen }: { chosen?: AssetClassData }) {
  if (!chosen) return null;
  return (
    <span className="block text-[11px] text-[var(--color-foreground-subtle)]">
      {chosen.hint} {chosen.year_one_note}
    </span>
  );
}

/**
 * Freezing the basis and choosing how year one is taken.
 *
 * The date asked for is the day the asset was ready and available for use, not
 * the day it was paid for. That is what the depreciation clock runs from, and
 * getting it wrong moves the entire deduction into a different tax year.
 */
function PlaceInServiceDialog({
  asset,
  onClose,
}: {
  asset: FixedAssetData;
  onClose: () => void;
}) {
  const place = usePlaceInService();
  const toast = useToast();
  const { error, wrap } = useFormError();

  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [businessUse, setBusinessUse] = useState('100');
  const [takeYearOne, setTakeYearOne] = useState(true);
  const [section179, setSection179] = useState(
    centsToDollarsInput(asset.capitalized_cents),
  );

  const usePct = Math.min(100, Math.max(1, Number(businessUse) || 100));
  const basis = Math.round(asset.capitalized_cents * (usePct / 100));
  const canTake179 = asset.section_179_eligible && usePct > 50;

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();

    const ok = await wrap(() =>
      place.mutateAsync({
        id: asset.id,
        placed_in_service_on: date,
        business_use_pct: usePct,
        // §179 where it reaches, bonus where it does not. The server enforces
        // both rules again; this just picks the door that works.
        section_179_cents:
          takeYearOne && canTake179
            ? Math.min(basis, dollarsToCents(section179) ?? 0)
            : 0,
        bonus_pct: takeYearOne && !canTake179 && asset.bonus_eligible ? 100 : 0,
      }),
    );

    if (ok) {
      toast.success(`${asset.name} is in service`);
      onClose();
    }
  }

  return (
    <Dialog open onClose={onClose} title={`Place ${asset.name} in service`} size="sm">
      <form className="space-y-3" onSubmit={handleSubmit}>
        <div className="rounded-md bg-[var(--color-surface-muted)] px-3 py-2 text-xs">
          <div className="flex justify-between">
            <span className="text-[var(--color-foreground-muted)]">Capitalized so far</span>
            <span className="font-semibold tabular-nums">
              {dollars(asset.capitalized_cents)}
            </span>
          </div>
          <p className="mt-1 text-[var(--color-foreground-subtle)]">{asset.year_one_note}</p>
        </div>

        <label className="block space-y-1">
          <span className="text-xs font-medium text-[var(--color-foreground-muted)]">
            Ready for use on
          </span>
          <Input
            type="date"
            required
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
          <span className="block text-[11px] text-[var(--color-foreground-subtle)]">
            The day it was ready to use, which is often not the day it was paid for. This sets the
            tax year the deduction lands in.
          </span>
        </label>

        <label className="block space-y-1">
          <span className="text-xs font-medium text-[var(--color-foreground-muted)]">
            Business use
          </span>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min={1}
              max={100}
              value={businessUse}
              onChange={(e) => setBusinessUse(e.target.value)}
              className="w-24"
            />
            <span className="text-xs text-[var(--color-foreground-muted)]">
              % · basis {dollars(basis)}
            </span>
          </div>
          {usePct <= 50 && asset.section_179_eligible ? (
            <span className="block text-[11px] text-[var(--color-warning,#b45309)]">
              At or below 50% business use, §179 is not available.
            </span>
          ) : null}
        </label>

        <label className="flex items-start gap-2 text-xs">
          <input
            type="checkbox"
            checked={takeYearOne}
            onChange={(e) => setTakeYearOne(e.target.checked)}
            className="mt-0.5"
            disabled={!asset.section_179_eligible && !asset.bonus_eligible}
          />
          <span>
            <span className="font-medium">Take it all in year one</span>
            <span className="block text-[var(--color-foreground-subtle)]">
              {canTake179
                ? 'Through §179, which is capped at your net business income for the year. Anything it cannot take carries forward.'
                : asset.bonus_eligible
                  ? 'Through bonus depreciation, which has no business-income limit.'
                  : 'Not available on 39-year property.'}
            </span>
          </span>
        </label>

        {takeYearOne && canTake179 ? (
          <label className="block space-y-1">
            <span className="text-xs font-medium text-[var(--color-foreground-muted)]">
              §179 amount
            </span>
            <Input
              value={section179}
              onChange={(e) => setSection179(e.target.value)}
              inputMode="decimal"
            />
            <span className="block text-[11px] text-[var(--color-foreground-subtle)]">
              Take less than the full basis to spread the deduction. §179 also reduces the QBI
              deduction, so the last dollar is worth a little less than your marginal rate.
            </span>
          </label>
        ) : null}

        {error ? <p className="text-sm text-[var(--color-danger)]">{error}</p> : null}
        <div className="flex justify-end">
          <Button type="submit" disabled={place.isPending}>
            {place.isPending ? 'Saving…' : 'Place in service'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
